import React, { useEffect, useRef, useState } from "react";

import AppLayout from "@/Layouts/AppLayout";
import { Dropdown, EmptyState } from "@/Components";

import { X, Search, MoreVertical } from "react-feather";
import { usePage } from "@inertiajs/inertia-react";
import { Inertia } from "@inertiajs/inertia";
import OrganizationLayout from "@/Layouts/OrganizationLayout";
import { atom, useAtom } from "jotai";
import { debounce } from "lodash";
import { SCOPE_OPTIONS } from "@/Enums/scopes";
import {
  LabeledInput,
  LabeledMultiSelectAutocomplete,
  LabeledSelectAutocomplete,
} from "@/Components/Form";
import { toast } from "react-toastify";
import { Teams as TeamsIcon } from "@/Icons";
import LayoutIcon from "@/Components/LayoutIcon";
import SuperuserQuestionRow from "@/Pages/Superuser/Questions/SuperuserQuestionRow";
import { ActionMenu } from "@/Components/ActionMenu";
import { useSetAtom } from "jotai/index";
import { isNewActivityOpenAtom } from "@/Pages/Reporting/Owner/Index";
import { LoadingProvider } from "@/Contexts/LoadingContext";
import NewActivityChooseType from "@/Components/Team/NewActivityChooseType";

const searchAtom = atom("");
const sortAtom = atom("created.asc");
const scopesAtom = atom(null);
const tagsAtom = atom(null);

export default function Index({ tags }) {
  const questions = usePage().props?.questions?.data || [];
  const organizations = usePage().props?.organizations?.data || [];
  const createdQuestionId =
    usePage().props?.custom_response_data?.created_question_id || false;

  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [targetOrganization, setTargetOrganization] = useState(null);

  let [modal, setModal] = useState({
    open: false,
    title: "",
    content: "",
    class: "",
  });

  const questionsSortedByNewlyCreated = () => {
    // return questions
    if (!createdQuestionId) {
      return questions;
    }

    return questions.sort((q) => (createdQuestionId === q.id ? -1 : 1));
  };

  const questionComponents = questionsSortedByNewlyCreated()
    .map((question, i) => showFilteredQuestion(question, question.id))
    .filter((n) => n);

  function showFilteredQuestion(question, id) {
    return (
      <div key={`question_${id}`} className="relative">
        <input
          type="checkbox"
          checked={selectedRows.includes(id)}
          onChange={() => handleSelectRow(id)}
          className="absolute left-4 top-4 z-10"
        />
        <SuperuserQuestionRow
          team={null}
          question={question}
          isNew={createdQuestionId === id}
          onDelete={(questionId) => {
            setSelectedRows([questionId]);
            setIsDeleting(true);
          }}
          onAttach={(questionId) => {
            setSelectedRows([questionId]);
            openModal(
              "Add to organization",
              "You're about to add this question to an organization",
              "Adding this question to an organization will make a copy of this question and it will be available for use on any team within the organization.",
              "bg-blue",
            );
          }}
        />
      </div>
    );
  }

  function openModal(l, t, d, c) {
    setModal({ open: true, label: l, title: t, content: d, class: c });
  }

  function closeModal() {
    setModal({ open: false, label: "", title: "", content: "", class: "" });
  }

  // Add a new function to handle row selection
  function handleSelectRow(id) {
    setSelectedRows((prevSelectedRows) => {
      if (prevSelectedRows.includes(id)) {
        return prevSelectedRows.filter((rowId) => rowId !== id);
      } else {
        return [...prevSelectedRows, id];
      }
    });
  }

  const handleDelete = () => {
    const id = toast.loading("Please wait...");

    Inertia.delete(route("admin.question.destroy"), {
      data: { questions: selectedRows },
      onSuccess: () => {
        toast.update(id, {
          render: "The question has been removed from your team!",
          type: "success",
          isLoading: false,
          autoClose: 5000,
        });
      },
      onError: () => {
        toast.update(id, {
          render: "Something went wrong when removing the question",
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      },
      onFinish: () => {
        setIsDeleting(false);
      },
    });
  };

  function handleAddToOrganization() {
    const id = toast.loading("Please wait...");

    Inertia.post(
      route("admin.question.attach", {
        organization: targetOrganization,
      }),
      { questions: selectedRows },
      {
        onSuccess: () => {
          closeModal();
          toast.update(id, {
            render: `Question added successfully to organization`,
            type: "success",
            isLoading: false,
            autoClose: 5000,
          });
        },
      },
    );
  }

  const actionMenuItems = [
    {
      label: "Add to organization",
      callback: () =>
        openModal(
          "Add to organization",
          "You're about to add the selected question(s) to an organization",
          "Adding these question(s) to an organization will make a copy of the question and it will be available for use on any team within the organization.",
          "bg-blue",
        ),
    },
    {
      label: "Delete activity",
      callback: () => setIsDeleting(true),
    },
  ];

  const actionMenuRef = useRef(null);
  const setNewActivityDialogOpen = useSetAtom(isNewActivityOpenAtom);

  return (
    <div className="mb-8">
      <ActivityFilter tagData={tags.data} />
      {questionComponents.length === 0 ? (
        <EmptyState
          title="You have no global activities yet"
          text="Create activity templates that can be attached to any organization"
          buttonText="New activity"
          buttonOnClick={() => setNewActivityDialogOpen(true)}
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid gap-2">{questionComponents}</div>
          <div className="shadow-small flex h-12 items-center justify-center gap-4 self-start bg-white py-2 pl-4 pr-2">
            <input
              type="checkbox"
              checked={questionComponents.length === selectedRows.length}
              onChange={() => {
                if (questionComponents.length === selectedRows.length) {
                  setSelectedRows([]);
                } else {
                  setSelectedRows(questions.map((q) => q.id));
                }
              }}
            />
            <button
              className="hover-circle relative"
              onClick={(e) => {
                e.preventDefault();
                actionMenuRef.current.toggleMenu();
              }}
            >
              <MoreVertical size={24} className="shrink-0 cursor-pointer" />
              <ActionMenu ref={actionMenuRef} items={actionMenuItems} />
            </button>
          </div>
        </div>
      )}

      {modal.open && (
        <div className="fixed left-0 top-0 z-10 flex h-full w-full flex-col justify-center bg-black bg-opacity-50 lg:pl-[240px] 4xl:pl-[300px]">
          <div
            className="absolute left-0 top-0 h-full w-full"
            onClick={closeModal}
          />
          <div className="px-4 lg:px-16 4xl:pl-32">
            <div className="mx-auto 4xl:mx-0 4xl:max-w-screen-3xl ">
              <div
                className={`relative mx-auto w-full max-w-2xl bg-white p-8 align-middle md:p-16`}
              >
                <div className="mb-2.5 flex items-center justify-between">
                  <span className="rounded-br-2xl bg-apricot px-4 py-2 text-white">
                    {modal.label}
                  </span>
                  <X
                    className="h-12 w-12 cursor-pointer p-2"
                    onClick={closeModal}
                  />
                </div>
                <h3 className="mb-3 text-xl font-semibold">{modal.title}</h3>
                <p className="mb-4">{modal.content}</p>

                <LabeledSelectAutocomplete
                  title="Choose organization"
                  options={organizations.map((organization) => ({
                    value: organization.id,
                    name: `${organization.name}, ${organization.organization_number}`,
                  }))}
                  onSelect={(val) => setTargetOrganization(val)}
                  defaultValue={targetOrganization}
                />

                <div className="mt-5 flex w-full justify-end border-t pt-8">
                  <a
                    className="cursor-pointer py-4 text-blue underline"
                    onClick={closeModal}
                  >
                    Never mind
                  </a>

                  <button
                    className="btn ml-3 self-end"
                    onClick={handleAddToOrganization}
                  >
                    Add to organization
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isDeleting && (
        <div className="fixed left-0 top-0 z-20 flex h-full w-full flex-col justify-center bg-black bg-opacity-50">
          <div
            className="absolute left-0 top-0 h-full w-full"
            onClick={() => setIsDeleting(false)}
          />
          <div className="relative mx-auto w-full max-w-2xl bg-white p-8 align-middle md:p-16">
            <X
              className="absolute right-0 top-0 h-12 w-12 cursor-pointer p-2"
              onClick={() => setIsDeleting(false)}
            />
            <h3 className="mb-3 text-xl font-semibold">
              Are you sure that you want to continue?
            </h3>
            <p>
              Deleting this question will only delete it from Superuser and not
              existing organizations where it might have been copied to.
            </p>

            <div className="mt-5 flex w-full justify-end border-t pt-8">
              <a
                className="cursor-pointer py-4 text-blue underline"
                onClick={() => setIsDeleting(false)}
              >
                Cancel
              </a>

              <button className="btn ml-3 self-end" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ActivityFilter = ({ tagData }) => {
  const [search, setSearch] = useAtom(searchAtom);
  const [sort, setSort] = useAtom(sortAtom);
  const [scopes, setScopes] = useAtom(scopesAtom);
  const [tags, setTags] = useAtom(tagsAtom);

  const sortOptions = [
    { value: "title.asc", label: "Name A-Ö" },
    { value: "title.desc", label: "Name Ö-A" },
    { value: "created_at.desc", label: "Newest" },
    { value: "created_at.asc", label: "Oldest" },
    { value: "updated_at.desc", label: "Updated most recent" },
    { value: "updated_at.asc", label: "Updated least recent" },
  ];

  const tagOptions = tagData.map((tag) => {
    return {
      value: String(tag.id),
      label: tag.name,
    };
  });

  const scopeRef = useRef(null);
  const tagRef = useRef(null);

  const handleSearchAndSort = React.useRef(
    debounce((search, sort, scopes, tags) => {
      Inertia.reload({
        only: ["questions"],
        data: {
          search: search,
          sort: sort,
          scopes: scopes?.join(","),
          tags: tags?.join(","),
        },
      });
    }, 500),
  ).current;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (!params.get("scopes") || scopes === null) {
      const initialSearch = params.get("search") || "";
      const initialSort = params.get("sort") || "";
      const initialScopes = params.get("scopes")
        ? params.get("scopes").split(",")
        : ["other", ...SCOPE_OPTIONS.map((s) => s.value)];
      const initialTags = params.get("tags")
        ? params.get("tags").split(",")
        : ["other", ...tagOptions.map((s) => s.value)];

      setSearch(initialSearch);
      setSort(initialSort);
      setScopes(initialScopes);
      setTags(initialTags);

      scopeRef.current.setStateOptions((prev) => {
        return prev.map((scope) => {
          return {
            ...scope,
            checked: initialScopes.includes(scope.value),
          };
        });
      });

      tagRef.current.setStateOptions((prev) => {
        return prev.map((tag) => {
          return {
            ...tag,
            checked: initialTags.includes(tag.value),
          };
        });
      });

      refreshData(initialSearch, initialSort, initialScopes, initialTags);
    }
  }, []);

  const refreshData = (search, sort, scopes, tags) => {
    handleSearchAndSort.cancel();
    handleSearchAndSort(search, sort, scopes, tags);
  };

  return (
    <div className="mb-4 flex flex-col items-end justify-between gap-4 sm:flex-row sm:items-center">
      <div className="flex w-full flex-col gap-4 sm:w-auto sm:flex-row sm:items-end">
        <LabeledMultiSelectAutocomplete
          title="Categories and modules"
          className="w-full sm:w-auto"
          ref={tagRef}
          placeholder="All"
          options={[{ value: "other", label: "Other" }, ...tagOptions]}
          defaultValue={tags || []}
          onSelect={(val) => {
            const values = val.map((v) => v.value);
            setTags(values);
            refreshData(search, sort, scopes, values);
          }}
        />

        <LabeledMultiSelectAutocomplete
          className="w-full sm:w-auto"
          ref={scopeRef}
          placeholder="Scopes"
          options={[{ value: "other", label: "Other" }, ...SCOPE_OPTIONS]}
          defaultValue={scopes || []}
          onSelect={(val) => {
            const values = val.map((v) => v.value);
            setScopes(values);
            refreshData(search, sort, values, tags);
          }}
        />

        <LabeledInput
          value={search}
          type="search"
          placeholder="Search"
          className="w-full sm:w-auto"
          unit={search === "" ? <Search className="relative -top-1" /> : null}
          onChange={(e) => {
            const value = e.target.value;
            setSearch(e.target.value);
            refreshData(value, sort, scopes, tags);
          }}
        />
      </div>

      <Dropdown
        options={sortOptions}
        title="Sort on"
        placeholder="Name A-Ö"
        onSelect={(value) => {
          setSort(value);
          refreshData(search, value, scopes, tags);
        }}
      />
    </div>
  );
};

const HeaderActions = () => {
  const setNewActivityDialogOpen = useSetAtom(isNewActivityOpenAtom);

  return (
    <button
      className="btn-blue shrink-0 grow-0"
      onClick={() => setNewActivityDialogOpen(true)}
    >
      New activity
    </button>
  );
};

Index.layout = (page) => {
  return (
    <AppLayout title="Global activities">
      <OrganizationLayout
        header={"Global activities"}
        byline={`Create activity templates that can be attached to any organization`}
        action={<HeaderActions />}
        icon={
          <LayoutIcon>
            <TeamsIcon size={40} className="text-black" />
          </LayoutIcon>
        }
      >
        {page}
      </OrganizationLayout>
      <LoadingProvider>
        <NewActivityChooseType source="admin" />
      </LoadingProvider>
    </AppLayout>
  );
};
